// #SAFETY_DOCLIB_V1 (2026-10-12) — Bill: "SAFETY DOC LIBRARY: Need to build out a library of documents that we could
// pull blank copies ... We can load EMR DOCS, OSHA Docs."
//
// Folders:  Blank Forms  — everyone with Safety (crews/filers included): built-in DUC blanks (JSA from the DOPE
//                          template, DVIR scan) + anything Safety loads.
//           EMR / OSHA / Other — Safety view / edit only (OSHA 300 logs carry injured employees' names; EMR letters
//                          are insurance documents). Filers never see these folders.
// Files:    public 'safety-files' bucket under doclib/   (same bucket + storage rules as MSDS)
// Catalog:  app_storage 'safety-doclib' = [{ id, name, cat, year, file, path, size, type, hash, addedBy, addedAt }]
// Safety edit loads / renames / moves / re-years / removes. Same load pattern as MsdsLibrary (#MSDS_LIBRARY_V1).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { JSA_STARTERS, JSA_TPL_KEY, printBlankJsa } from './JsaParts';
import { makeSafetySigner, openSafetyFile } from "./safetyFiles";   // #SAFETY_PRIVATE_V1

export const DOCLIB_KEY = 'safety-doclib';
const BUCKET = 'safety-files';
const OK_EXT = /\.(pdf|docx?|xlsx?|png|jpe?g)$/i;
const P = { bg:'#0E1113', card:'#171B1F', cardHi:'#1F252B', line:'#2C343C', text:'#F4F6F7', muted:'#95A1A9', accent:'#FDE047', accent2:'#FF6A00', ink:'#0A0C0E', green:'#34D399', red:'#F87171', blue:'#5B8DEF', purple:'#A78BFA' };

export const DOC_CATS = [
  { id:'forms', label:'Blank Forms', color:P.accent, all:true, hint:'Blank copies to print or hand out — crews can pull these.' },
  { id:'emr',   label:'EMR Docs',    color:P.green,  hint:'Experience Modification Rate letters / worksheets by year — what insurers and prequal forms ask for.' },
  { id:'osha',  label:'OSHA Docs',   color:P.blue,   hint:'OSHA 300 / 300A / 301 logs, posters, citations, programs. Safety-only — 300 logs carry names.' },
  { id:'other', label:'Other',       color:P.purple, hint:'Anything else Safety wants on file.' },
];
// Built-in blanks — always in Blank Forms, nothing to upload.
const BUILTIN = [
  { id:'b-jsa',  name:'Job Safety Analysis (JSA)', note:'Pick a task, then fill it out & sign on the phone (saves, e-mails a signed PDF) — or print it for paper', jsa:true },
  { id:'b-dvir', name:"Driver's Vehicle Inspection Report (DVIR)", note:'Master form — fill out & submit here or on the Fleet dashboards (kept 90 days, defects open a work order); edits to the master change it everywhere', url:'/forms/DUC-DVIR-blank.pdf', dvir:true },
];

const extOf = (n) => ((String(n || '').match(/\.([a-z0-9]{2,5})$/i) || [])[1] || 'pdf').toLowerCase();
const ctypeOf = (ext) => ({ pdf:'application/pdf', doc:'application/msword', docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls:'application/vnd.ms-excel', xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', png:'image/png', jpg:'image/jpeg', jpeg:'image/jpeg' }[ext] || 'application/octet-stream');
const kb = (b) => !b ? '' : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const cleanName = (fn) => String(fn || '').replace(/^.*[\\/]/, '').replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_+]/g, ' ').replace(/\s{2,}/g, ' ').trim() || 'Document';
const yearOf = (fn) => { const m = String(fn || '').match(/\b(20[0-4]\d)\b/); return m ? m[1] : ''; };
async function sha(file) {
  try { const h = await crypto.subtle.digest('SHA-256', await file.arrayBuffer()); return Array.from(new Uint8Array(h)).map(x => x.toString(16).padStart(2, '0')).join('').slice(0, 40); }
  catch { return ''; }
}

export default function SafetyDocLibrary({ sg, ss, supaUrl, supaKey, getToken, userUpn, editable, restricted, onFillJsa, onFillDvir, onPrintDvir, onEditDvir }) {   // #DVIR_MASTER_V1
  const me = String(userUpn || '').trim().toLowerCase();
  const cats = restricted ? DOC_CATS.filter(c => c.all) : DOC_CATS;
  const [cat, setCat] = useState('forms');
  const [list, setList] = useState(null);
  const [tpls, setTpls] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ name:'', year:'', cat:'' });
  const [delId, setDelId] = useState(null);
  const [jsaPick, setJsaPick] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const filesRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => { listRef.current = list; }, [list]);
  useEffect(() => { (async () => {
    try { const v = await sg(DOCLIB_KEY); setList(Array.isArray(v) ? v : []); } catch { setList([]); }
    try { const t = await sg(JSA_TPL_KEY); setTpls(Array.isArray(t) ? t : []); } catch {}
  })(); }, [sg]);

  // #SAFETY_PRIVATE_V1 — bucket is private; View / Download sign the link with the viewer's sign-in first
  const signSafety = makeSafetySigner(supaUrl, supaKey, getToken);
  const dlName = (x) => `${x.name}.${x.type || 'pdf'}`.replace(/[\\/:*?"<>|#%]/g, '-');
  const hdr = async (extra) => { const tok = getToken ? await getToken() : null; return { apikey: supaKey, Authorization: `Bearer ${tok || supaKey}`, ...(extra || {}) }; };
  const fresh = async () => { try { const v = await sg(DOCLIB_KEY); if (Array.isArray(v)) return v; } catch {} return listRef.current || []; };
  const saveList = async (next) => { listRef.current = next; setList(next); try { await ss(DOCLIB_KEY, next); } catch (e) { setMsg({ ok:false, text:'Could not save the list: ' + (e.message || e) }); } };

  const counts = useMemo(() => { const c = {}; (list || []).forEach(x => { c[x.cat] = (c[x.cat] || 0) + 1; }); c.forms = (c.forms || 0) + BUILTIN.length; return c; }, [list]);
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (list || []).filter(x => x.cat === cat)
      .filter(x => !words.length || words.every(w => `${x.name} ${x.file} ${x.year || ''}`.toLowerCase().includes(w)))
      .sort((a, b) => String(b.year || '').localeCompare(String(a.year || '')) || String(a.name).localeCompare(String(b.name), undefined, { numeric:true, sensitivity:'base' }));
  }, [list, cat, q]);
  const builtinShown = cat === 'forms' ? BUILTIN.filter(b => !q.trim() || b.name.toLowerCase().includes(q.trim().toLowerCase())) : [];
  const C = DOC_CATS.find(c => c.id === cat) || DOC_CATS[0];

  const load = async (files) => {
    if (!editable) return;
    const tok = getToken ? await getToken() : null;
    if (!tok) { setMsg({ ok:false, text:'Your sign-in has expired — reload DOPE, then load again.' }); return; }
    const good = files.filter(f => OK_EXT.test(f.name));
    if (!good.length) { setMsg({ ok:false, text:'Nothing to load — PDF, Word, Excel or image files only.' }); return; }
    setMsg(null);
    let cur = (await fresh()).slice();
    const seen = new Set(cur.map(x => x.hash).filter(Boolean));
    let added = 0, dup = 0; const failed = [];
    for (let i = 0; i < good.length; i++) {
      const f = good[i];
      setBusy(`Loading ${i + 1} of ${good.length} — ${f.name}`);
      const h = await sha(f);
      if (h && seen.has(h)) { dup++; continue; }
      const ext = extOf(f.name);
      const path = `doclib/${cat}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
      try {
        const res = await fetch(`${supaUrl}/storage/v1/object/${BUCKET}/${path}`, { method:'POST', headers: await hdr({ 'Content-Type': f.type || ctypeOf(ext), 'x-upsert':'true' }), body: f });
        if (!res.ok) { failed.push(`${f.name} (${res.status})`); if (failed.length >= 3 && !added) break; continue; }
      } catch (e) { failed.push(`${f.name} (${e.message || e})`); continue; }
      cur.push({ id:`doc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: cleanName(f.name), cat, year: yearOf(f.name), file: f.name, path, size: f.size, type: ext, hash: h, addedBy: me, addedAt: new Date().toISOString() });
      if (h) seen.add(h); added++;
    }
    await saveList(cur);
    setBusy('');
    setMsg({ ok: !failed.length, text: `Added ${added} to ${C.label}` + (dup ? ` · ${dup} duplicate${dup === 1 ? '' : 's'} skipped` : '') + (failed.length ? ` · ${failed.length} failed: ${failed.slice(0, 3).join('; ')}` : '') });
  };
  const saveEdit = async (id) => {
    const nm = edit.name.trim(); setEditId(null); if (!nm) return;
    await saveList((await fresh()).map(x => x.id === id ? { ...x, name: nm, year: edit.year.trim(), cat: edit.cat || x.cat } : x));
  };
  const remove = async (x) => {
    setDelId(null);
    await saveList((await fresh()).filter(y => y.id !== x.id));
    try { await fetch(`${supaUrl}/storage/v1/object/${BUCKET}/${x.path}`, { method:'DELETE', headers: await hdr() }); } catch {}
  };

  const inp = { background:P.card, border:`1px solid ${P.line}`, color:P.text, borderRadius:8, padding:'9px 11px', fontSize:14, outline:'none', boxSizing:'border-box', width:'100%' };
  const btn = (c, solid) => ({ background: solid ? c : 'transparent', color: solid ? P.ink : c, border:`1px solid ${c}`, borderRadius:8, padding:'6px 11px', fontSize:12, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap', textDecoration:'none', display:'inline-block' });
  const tag = (c) => ({ fontSize:9.5, fontWeight:900, letterSpacing:.4, color:c, border:`1px solid ${c}88`, borderRadius:5, padding:'1px 5px', textTransform:'uppercase' });
  const row = { display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderBottom:`1px solid ${P.line}`, flexWrap:'wrap' };
  const allTpls = [...tpls, ...JSA_STARTERS];

  return (
    <div style={{ color:P.text }}
      onDragOver={e => { if (editable) { e.preventDefault(); setDragOver(true); } }} onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); if (editable) load(Array.from(e.dataTransfer.files || [])); }}>
      {/* folder tabs */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
        {cats.map(c => (
          <button key={c.id} onClick={() => { setCat(c.id); setQ(''); setEditId(null); setDelId(null); }}
            style={{ ...btn(c.color, cat === c.id), padding:'9px 14px', fontSize:13 }}>
            {c.label} <span style={{ opacity:.75, fontWeight:700 }}>· {counts[c.id] || 0}</span>
          </button>
        ))}
      </div>
      <div style={{ fontSize:12.5, color:P.muted, marginBottom:10 }}>{C.hint}</div>

      {editable && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', background:P.cardHi, border:`1px ${dragOver ? 'dashed' : 'solid'} ${dragOver ? P.accent : P.line}`, borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
          <span style={{ fontSize:11, fontWeight:900, letterSpacing:.6, textTransform:'uppercase', color:P.accent }}>Safety · manage</span>
          <button disabled={!!busy} onClick={() => filesRef.current && filesRef.current.click()} style={btn(C.color, true)}>+ Add to {C.label}</button>
          <span style={{ fontSize:11.5, color:P.muted }}>or drag files onto this page · PDF / Word / Excel / images · duplicates skipped · a year in the file name is picked up</span>
          <input ref={filesRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; load(fs); }} style={{ display:'none' }} />
        </div>
      )}
      {busy && <div style={{ fontSize:12.5, color:P.accent, fontWeight:800, marginBottom:10 }}>{busy}</div>}
      {msg && <div onClick={() => setMsg(null)} style={{ fontSize:12.5, fontWeight:700, marginBottom:10, padding:'8px 11px', borderRadius:8, cursor:'pointer', color: msg.ok ? P.green : P.red, background:`${msg.ok ? P.green : P.red}14`, border:`1px solid ${msg.ok ? P.green : P.red}55` }}>{msg.text}</div>}

      <input value={q} onChange={e => setQ(e.target.value)} placeholder={`🔎 Search ${C.label}…`} style={{ ...inp, marginBottom:10 }} />

      <div style={{ border:`1px solid ${P.line}`, borderRadius:12, overflow:'hidden' }}>
        {builtinShown.map(b => (
          <div key={b.id} style={{ ...row, background:`${P.accent}08` }}>
            <div style={{ flex:'1 1 240px', minWidth:0 }}>
              <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}><span style={{ fontWeight:800, fontSize:13.5 }}>{b.name}</span><span style={tag(P.accent)}>DUC blank</span></div>
              <div style={{ fontSize:11, color:P.muted, marginTop:2 }}>{b.note}</div>
            </div>
            {b.jsa ? (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                <select value={jsaPick} onChange={e => setJsaPick(e.target.value)} style={{ ...inp, width:'auto', padding:'6px 8px', fontSize:12 }}>
                  <option value="">Blank — no task</option>
                  {allTpls.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {onFillJsa && <button onClick={() => onFillJsa(allTpls.find(t => t.id === jsaPick) || null)} style={btn(P.green, true)}>Fill out &amp; sign</button>}{/* #SAFETY_JSA_PAPER_V1 */}
                <button onClick={() => printBlankJsa({ template: allTpls.find(t => t.id === jsaPick) || null })} style={btn(P.accent)}>Print blank</button>
              </div>
            ) : (
              <div style={{ display:'flex', gap:6 }}>
                {b.dvir && onFillDvir && <button onClick={() => onFillDvir('pre')} style={btn(P.green, true)}>Fill out &amp; submit</button>}
                {b.dvir && onPrintDvir && <button onClick={onPrintDvir} style={btn(P.accent)}>Print blank</button>}
                {b.dvir && onEditDvir && <button onClick={onEditDvir} style={btn(P.purple)}>Edit master</button>}
                <a href={b.url} target="_blank" rel="noopener noreferrer" style={btn(P.muted)} title="The old paper pad, scanned">{b.dvir ? 'Original scan' : 'View / Print'}</a>
              </div>
            )}
          </div>
        ))}
        {list == null ? <div style={{ padding:18, color:P.muted }}>Loading…</div>
          : shown.map(x => (
            <div key={x.id} style={row}>
              {editId === x.id ? (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', flex:1, alignItems:'center' }}>
                  <input autoFocus value={edit.name} onChange={e => setEdit(s => ({ ...s, name:e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') saveEdit(x.id); if (e.key === 'Escape') setEditId(null); }} style={{ ...inp, flex:'2 1 200px', width:'auto', padding:'6px 8px', fontSize:13 }} />
                  <input value={edit.year} placeholder="Year" onChange={e => setEdit(s => ({ ...s, year:e.target.value.replace(/[^0-9]/g, '').slice(0, 4) }))} style={{ ...inp, width:80, padding:'6px 8px', fontSize:13 }} />
                  <select value={edit.cat} onChange={e => setEdit(s => ({ ...s, cat:e.target.value }))} style={{ ...inp, width:'auto', padding:'6px 8px', fontSize:13 }}>{DOC_CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select>
                  <button onClick={() => saveEdit(x.id)} style={btn(P.green, true)}>Save</button>
                  <button onClick={() => setEditId(null)} style={btn(P.muted)}>Cancel</button>
                </div>
              ) : (
                <>
                  <div style={{ flex:'1 1 240px', minWidth:0 }}>
                    <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ fontWeight:800, fontSize:13.5 }}>{x.name}</span>
                      {x.year && <span style={tag(C.color)}>{x.year}</span>}
                      {x.type && x.type !== 'pdf' && <span style={tag(P.muted)}>{x.type}</span>}
                    </div>
                    <div style={{ fontSize:10.5, color:P.muted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{x.file} · {kb(x.size)}</div>
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    <a href="#view" onClick={e => { e.preventDefault(); if (x && x.path) openSafetyFile(signSafety, x.path); }} style={btn(P.blue)}>View / Print</a>
                    <a href="#download" onClick={e => { e.preventDefault(); if (x && x.path) openSafetyFile(signSafety, x.path, dlName(x)); }} style={btn(P.muted)}>Download</a>
                    {editable && (delId === x.id
                      ? <><button onClick={() => remove(x)} style={btn(P.red, true)}>Remove</button><button onClick={() => setDelId(null)} style={btn(P.muted)}>Keep</button></>
                      : <>
                          <span title="Rename / year / move" onClick={() => { setEditId(x.id); setEdit({ name:x.name, year:x.year || '', cat:x.cat }); }} style={{ cursor:'pointer', color:P.muted, padding:'4px 5px' }}>✎</span>
                          <span title="Remove from library" onClick={() => setDelId(x.id)} style={{ cursor:'pointer', color:P.muted, padding:'4px 5px' }}>🗑</span>
                        </>)}
                  </div>
                </>
              )}
            </div>
          ))}
        {list != null && !shown.length && !builtinShown.length && (
          <div style={{ padding:'22px 16px', textAlign:'center', color:P.muted }}>
            {q ? <>Nothing in {C.label} matches "{q}".</> : editable ? <>Nothing in {C.label} yet — click <b style={{ color:C.color }}>+ Add to {C.label}</b> or drag files here.</> : <>Nothing in {C.label} yet.</>}
          </div>
        )}
      </div>
    </div>
  );
}
